import { AlertTriangle, Info, LifeBuoy, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useT } from "@/i18n/LanguageContext";

export function WarningBanner() {
  const t = useT();
  return (
    <div
      role="alert"
      className="glass-card"
      style={{
        padding: "clamp(0.85rem, 3vw, 1.25rem) clamp(0.85rem, 3vw, 1.5rem)",
        marginBottom: "1.5rem",
        border: "1px solid rgba(248,113,113,0.5)",
        background:
          "linear-gradient(135deg, rgba(248,113,113,0.08), rgba(251,146,60,0.05))",
        display: "flex",
        gap: "clamp(0.6rem, 2vw, 1rem)",
        alignItems: "flex-start",
      }}
    >
      <AlertTriangle
        size={22}
        style={{ color: "#f87171", flexShrink: 0, marginTop: 2 }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <strong style={{ color: "#fca5a5", fontSize: "0.95rem" }}>
          {t("warn.title")}
        </strong>
        <p style={{ color: "var(--text-primary)", fontSize: "0.88rem", lineHeight: 1.55, margin: 0 }}>
          <strong style={{ color: "#fca5a5" }}>{t("warn.mix.bold")}</strong>{" "}
          {t("warn.mix.body")}
        </p>
        <p style={{ color: "var(--text-primary)", fontSize: "0.88rem", lineHeight: 1.55, margin: 0 }}>
          <strong style={{ color: "#fca5a5" }}>{t("warn.stop.bold")}</strong>{" "}
          {t("warn.stop.body")}
        </p>
        <p style={{ color: "var(--text-primary)", fontSize: "0.88rem", lineHeight: 1.55, margin: 0 }}>
          <strong style={{ color: "#fca5a5" }}>{t("warn.gabapentinoid.bold")}</strong>{" "}
          {t("warn.gabapentinoid.body")}
        </p>
        <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", margin: 0 }}>
          {t("warn.disclaimer")}
        </p>
      </div>
    </div>
  );
}

export function EmergencyBox() {
  const t = useT();
  const items: Array<[string, string]> = [
    ["emergency.signs.label", "emergency.signs.body"],
    ["emergency.naloxone.label", "emergency.naloxone.body"],
    ["emergency.flumazenil.label", "emergency.flumazenil.body"],
    ["emergency.counterfeit.label", "emergency.counterfeit.body"],
    ["emergency.risk.label", "emergency.risk.body"],
  ];
  return (
    <div
      role="note"
      aria-labelledby="benzo-emergency-title"
      className="glass-card"
      style={{
        padding: "clamp(0.85rem, 3vw, 1.25rem) clamp(0.85rem, 3vw, 1.5rem)",
        marginBottom: "1.5rem",
        border: "1px solid var(--accent-border)",
        display: "flex",
        gap: "clamp(0.6rem, 2vw, 1rem)",
        alignItems: "flex-start",
      }}
    >
      <LifeBuoy size={20} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", minWidth: 0 }}>
        <strong id="benzo-emergency-title" style={{ color: "var(--accent-strong)", fontSize: "0.95rem" }}>
          {t("emergency.title")}
        </strong>
        <ul
          style={{
            margin: 0,
            paddingLeft: "1.1rem",
            color: "var(--text-secondary)",
            fontSize: "0.84rem",
            lineHeight: 1.6,
            display: "flex",
            flexDirection: "column",
            gap: "0.45rem",
          }}
        >
          {items.map(([labelK, bodyK]) => (
            <li key={labelK}>
              <strong style={{ color: "var(--text-primary)" }}>
                {t(labelK as Parameters<typeof t>[0])}
              </strong>{" "}
              {t(bodyK as Parameters<typeof t>[0])}
            </li>
          ))}
        </ul>
        <Link
          to="/notfall"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            alignSelf: "flex-start",
            color: "var(--accent-strong)",
            fontSize: "0.84rem",
            fontWeight: 600,
            textDecoration: "none",
            padding: "0.35rem 0.1rem",
            minHeight: "44px",
          }}
        >
          {t("emergency.link")}
          <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

export function LegendFooter() {
  const t = useT();
  const items: Array<[string, string, string?]> = [
    ["legend.dot.label", "legend.dot.body"],
    ["legend.equiv.label", "legend.equiv.body"],
    ["legend.typicalDose.label", "legend.typicalDose.body"],
    ["legend.zdrug.label", "legend.zdrug.body"],
    ["legend.class.label", "legend.class.body"],
    ["legend.metabolite.label", "legend.metabolite.body"],
    ["legend.midazolam.label", "legend.midazolam.body", "#fca5a5"],
    ["legend.clonazepam.label", "legend.clonazepam.body"],
    ["legend.passion.label", "legend.passion.body"],
    ["legend.flunitrazepam.label", "legend.flunitrazepam.body"],
    ["legend.noequiv.label", "legend.noequiv.body"],
  ];
  return (
    <div
      className="glass-card"
      style={{
        padding: "1.25rem 1.5rem",
        marginTop: "2rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.85rem",
      }}
    >
      <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
        <Info size={16} style={{ color: "var(--accent-cyan)" }} />
        <strong style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          {t("legend.title")}
        </strong>
      </div>

      <ul
        style={{
          margin: 0,
          paddingLeft: "1.1rem",
          color: "var(--text-muted)",
          fontSize: "0.82rem",
          lineHeight: 1.6,
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
        }}
      >
        {items.map(([labelK, bodyK, color]) => (
          <li key={labelK}>
            <strong style={{ color: color ?? "var(--text-secondary)" }}>
              {t(labelK as Parameters<typeof t>[0])}
            </strong>{" "}
            {t(bodyK as Parameters<typeof t>[0])}
          </li>
        ))}
      </ul>

    </div>
  );
}
